import { BaseComponent, BlueprintLocation, Building, LevelModel, PhotoModel, RoomModel } from '../types/protocols';
import { RoomImpl } from '../types/implementations';
import { resolveComponentLocation } from './ComponentRoomUtils';
import { levelDisplayName } from './levelDisplayName';

/**
 * The room's pin as a component's position: where a component that belongs to
 * a room is shown on the room's level drawing when it has no pin of its own
 * there. A room is specific enough to find the component by, so every "Hitta
 * komponenten" surface (work-order pages, the felanmälan card, the component
 * page) uses this instead of showing no drawing at all. Walls, floors and
 * ceilings never have pins of their own; openings, locks and never-placed
 * utilities often don't.
 *
 * Null when the component resolves to no room, the room has no level, the
 * level has no current drawing, the room is not placed on it, or the component
 * already has its own pin on that drawing. Shared by iOS
 * (`ComponentLocationsView`) and the guest API (`resolveComponentLocations`).
 */
export interface RoomDrawingMarker {
    /** The room's marker on the drawing, in the drawing's pixel space. */
    location: BlueprintLocation;
    image: PhotoModel;
    /** The level's current drawing key the marker sits on. */
    imageKey: string;
    room: RoomModel;
    level: LevelModel;
    /** "Rum 12 - Kök": the caption, so the dot reads as the room. */
    roomLabel: string;
    /** The level's display name. */
    levelLabel: string;
}

export const roomDrawingMarker = (component: BaseComponent, building: Building): RoomDrawingMarker | null => {
    const structure = building.model?.structure;
    const { roomKey, levelKey } = resolveComponentLocation(component, structure);
    const room = roomKey ? structure?.rooms?.[roomKey] : undefined;
    const level = levelKey ? structure?.levels?.[levelKey] : undefined;
    const imageKey = level?.currentBlueprintKey;
    if (!room || !level || !imageKey) return null;
    if (component.blueprintLocations?.[imageKey]) return null;
    const location = room.blueprintLocations?.[imageKey];
    const image = level.blueprints?.[imageKey];
    if (!location || !image?.storagePath) return null;
    return {
        location,
        image,
        imageKey,
        room,
        level,
        roomLabel: new RoomImpl(room).displayName(),
        levelLabel: levelDisplayName(level),
    };
};
